import React from 'react';

import closeIcon from "assets/icons/x-circle.svg";

import styles from './Modal.module.scss';

import cn from 'clsx';
import { useTranslation } from 'react-i18next';
import getSymbolFromCurrency from 'currency-symbol-map';
import lockIcon from "assets/icons/unlock-fill.svg";


type ModalContext = {
  onClose?: () => void;
  onSave?: () => void;
}
const ModalContext = React.createContext<ModalContext>({});

type HeaderProps = {
  children?: React.ReactNode;
  className?: string;
}

type ContentProps = {
  children: React.ReactNode;
  className?: string;
}

type FooterProps = {
  renderDefaultFooter?: boolean;
  renderCustomFooter?: boolean;
  children?: React.ReactNode;
  className?: string;
}

type TabProps<T extends string | number> = {
  tabs: T[];
  activeTab?: T | T[];
  canBeClicked?: boolean;
  className?: string;
  onClick?: (tab: T) => void;
  disabledTabs?: T[];
  isGeoTabs?: boolean;
  isLocaleTabs?: boolean;
  isCurrencyTabs?: boolean;
  isCurrencyTabsCheckbox?: boolean;
  isSegmentTabs?: boolean;
  isSmallModifier?: boolean;
};

type Props = {
  onClose?: () => void;
  onSave?: () => void;
  size?: 'sm' | 'md'
  children?: React.ReactNode;
  className?: string;
  closeOnOverlayClick?: boolean;
}

const MODAL_VERTICAL_OFFSET = 180;
const modalHeight = `calc(100vh - ${MODAL_VERTICAL_OFFSET}px)`;

function ModalHeader({ className, children }: HeaderProps) {
  const { onClose } = React.useContext(ModalContext);

  return (
    <div className={cn("modal-header", styles.modalHeader, className)}>
      <button className={styles.buttonX} onClick={onClose}>
        <img src={closeIcon} alt="" />
      </button>
      <div className={styles.modalTitle}>{children}</div>
    </div>
  );
}

function ModalContent({ children, className }: ContentProps) {
  return (
    <div className={cn(
      'modal-content',
      styles.modalContent,
      className
    )}>
      {children}
    </div>
  )
}

function ModalFooter({
  children,
  className,
}: FooterProps) {
  return (
    <div
      className={cn(
        'modal-footer',
        styles.modalFooter,
        className
      )}
    >
      {children}
    </div>
  )
}

function Modal({
  onClose,
  onSave,
  size = 'md',
  children,
  className,
  closeOnOverlayClick = true,
}: Props) {

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;

    if (closeOnOverlayClick && onClose && target.closest('.overlay')) {
      onClose();
    }
  }

  return (
    <ModalContext.Provider value={{ onClose, onSave }}>
      <div className={cn("modal-wrapper", styles.modalWrapper, className)} onMouseDown={handleOverlayClick}>
        <div
          className={cn("overlay", styles.overlay)}
        />
        <div
          className={cn(
            "modal-content-wrapper",
            styles.modalContentWrapper,
            size === "sm" && styles.modalContentWrapperSmall
          )}
          style={{
            height: modalHeight,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </ModalContext.Provider>
  );
}

function ModalTabs<T extends string | number>({
  tabs,
  activeTab,
  canBeClicked = true,
  onClick = () => {},
  disabledTabs = [],
  className,
  isGeoTabs = false,
  isLocaleTabs = false,
  isCurrencyTabs = false,
  isCurrencyTabsCheckbox = false,
  isSegmentTabs = false,
  isSmallModifier = false,
}: TabProps<T>) {
  const { t } = useTranslation();

  const handleClickTab = (v: T) => {
    onClick(v);
  };

  const isActiveItem = (tab: T) => {
    if (Array.isArray(activeTab)) {
      return activeTab.includes(tab);
    }

    return activeTab === tab;
  };

  const renderGeoTab = (c: string) => {
    if (c.toUpperCase() === "WW") {
      return <span className="mr-5">🌎</span>;
    }

    return (
      <img
        src={`https://flagcdn.com/w40/${c.toLowerCase()}.png`}
        alt={t(`${c}`)}
      />
    );
  };

  const renderLocaleTab = (locale: string) => {
    const countryCode = locale.split("-")[1];

    if (!countryCode || countryCode.toUpperCase() === "WW") {
      return <span className="mr-5">🌎</span>;
    }

    return (
      <img
        src={`https://flagpedia.net/data/flags/icon/16x12/${countryCode.toLowerCase()}.png`}
        alt={t(locale)}
      />
    );
  };

  const renderCurrencyTab = (c: string) => {
    return (
      <div className={styles.modalCurrencyTabSymbol}>
        {getSymbolFromCurrency(c)?.toString()}
      </div>
    );
  };

  return (
    <div
      className={cn(
        "modal-tabs-wrapper",
        styles.modalTabsWrapper,
        isSegmentTabs && styles.modalTabsWrapperSegment,
        isSmallModifier && styles.modalTabsWrapperSmall,
        isCurrencyTabs && styles.modalCurrencyTabs,
        className,
      )}
    >
      {tabs.map((el) => {
        const isDisabled = disabledTabs.includes(el);

        return (
          <div
            className={cn(
              styles.modalTabItem,
              isDisabled && styles.modalTabItemDisabled,
              isActiveItem(el) && styles.modalTabItemActive,
              isCurrencyTabsCheckbox && styles.modalTabItemCheckbox,
            )}
            key={el}
            onClick={() => handleClickTab(el)}
          >
            {isGeoTabs && renderGeoTab(el as string)}
            {isLocaleTabs && renderLocaleTab(el as string)}
            {isCurrencyTabs && (
              <div className="currency-tab-inner">
                {isCurrencyTabsCheckbox && (
                  <div
                    className={cn(
                      "def-check-control",
                      isActiveItem(el) && "checked",
                    )}
                  />
                )}

                {renderCurrencyTab(el as string)}
                <span className="tab-label">{t(`${el}`)}</span>
              </div>
            )}

            {isSegmentTabs && (
              <img
                className={cn("tab-lock-icon", isDisabled && "visible")}
                src={lockIcon}
                alt="locked"
              />
            )}
            {!isCurrencyTabs && <span className="tab-label">{t(`${el}`)}</span>}
          </div>
        );
      })}
    </div>
  );
};


Modal.Header = ModalHeader;
Modal.Content = ModalContent; 
Modal.Footer = ModalFooter;
Modal.Tabs = ModalTabs;

export { Modal };
